import { useState, useRef, useEffect } from "react";
import { submitKangstudyForm } from "@/lib/submit-form";
import { motion, AnimatePresence } from "motion/react";

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseTitle: string;
  courseId: string;
}

const propertyCountOptions = [
  { value: "무주택", label: "무주택" },
  { value: "1주택", label: "1주택" },
  { value: "2주택", label: "2주택" },
];

const genderOptions = [
  { value: "남", label: "남" },
  { value: "여", label: "여" },
];

const paymentOptions = [
  { value: "신규회원 330만원", label: "신규회원 330만원" },
  { value: "기존회원 55만원", label: "기존회원 55만원" },
];

const referralOptions = [
  { value: "블로그", label: "블로그" },
  { value: "유튜브", label: "유튜브" },
  { value: "지인", label: "지인" },
  { value: "인터넷 검색", label: "인터넷 검색" },
  { value: "기타", label: "기타" },
];

const experienceOptions = [
  { value: "초보", label: "초보" },
  { value: "중수", label: "중수" },
  { value: "고수", label: "고수" },
];

export default function RegistrationModal({
  isOpen,
  onClose,
  courseTitle,
  courseId,
}: RegistrationModalProps) {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [propertyCount, setPropertyCount] = useState("");
  const [gender, setGender] = useState("");
  const [payment, setPayment] = useState("");
  const [referral, setReferral] = useState("");
  const [experience, setExperience] = useState("");
  const [privacyAgree, setPrivacyAgree] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const resetForm = () => {
    setPropertyCount("");
    setGender("");
    setPayment("");
    setReferral("");
    setExperience("");
    setPrivacyAgree(false);
    setFormStatus("idle");
    setFormError("");
    formRef.current?.reset();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormStatus("success");
      resetForm();
      setTimeout(() => {
        setFormStatus("idle");
        onClose();
      }, 1500);
      return;
    }

    if (!privacyAgree) {
      setFormError("개인정보 수집 및 이용에 동의해주세요.");
      return;
    }

    setFormStatus("loading");
    setFormError("");

    try {
      const urlEncoded = new URLSearchParams();
      urlEncoded.append("name", String(formData.get("name") ?? ""));
      urlEncoded.append("phone", String(formData.get("phone") ?? ""));
      urlEncoded.append("nickname_region", String(formData.get("nickname_region") ?? ""));
      urlEncoded.append("property_count", propertyCount);
      urlEncoded.append("kakao_id", String(formData.get("kakao_id") ?? ""));
      urlEncoded.append("birth_year", String(formData.get("birth_year") ?? ""));
      urlEncoded.append("gender", gender);
      urlEncoded.append("payment_type", payment);
      urlEncoded.append("depositor_name", String(formData.get("depositor_name") ?? ""));
      urlEncoded.append("referral_path", referral);
      urlEncoded.append("auction_experience", experience);
      urlEncoded.append("privacy_agree", privacyAgree ? "동의" : "미동의");
      urlEncoded.append("course_title", courseTitle);
      urlEncoded.append("course_id", courseId);

      const { ok, error: submitError } = await submitKangstudyForm(
        "course_registration",
        Object.fromEntries(urlEncoded.entries()),
      );
      const serverMsg = submitError;

      if (ok) {
        setFormStatus("success");
        resetForm();
        setTimeout(() => {
          setFormStatus("idle");
          onClose();
        }, 2000);
      } else {
        if (serverMsg && String(serverMsg).includes("spam")) {
          setFormError("스팸으로 감지되었습니다. 다시 시도해주세요.");
        } else {
          setFormError(serverMsg || "제출 중 오류가 발생했습니다. 다시 시도해주세요.");
        }
        setFormStatus("error");
      }
    } catch {
      setFormError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
      setFormStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto py-8"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative w-full max-w-[600px] mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden z-10"
          >
            {/* Header */}
            <div className="bg-[#1C1917] px-6 py-5 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">수강 신청</h2>
                <p className="text-xs text-white/50 mt-0.5">{courseTitle}</p>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="닫기"
              >
                <i className="ri-close-line text-white"></i>
              </button>
            </div>

            {/* Form */}
            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form className="p-6 md:p-8">
              <div className="space-y-6">
                {/* 신청자명 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                    신청자명 (실명) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="실명을 입력해주세요"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                  />
                </div>

                {/* 휴대폰번호 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                    휴대폰번호 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="010-0000-0000"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                  />
                </div>

                {/* 닉네임_거주지역명 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                    닉네임 / 거주지역명
                  </label>
                  <input
                    type="text"
                    name="nickname_region"
                    placeholder="예: 강스터디 / 서울 강남구"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                  />
                </div>

                {/* 현재 보유채수 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                    현재 보유채수 <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {propertyCountOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border cursor-pointer transition-all ${
                          propertyCount === opt.value
                            ? "border-[#C4956A] bg-[#C4956A]/5 text-[#C4956A] font-medium"
                            : "border-[#E8E5E0] bg-white text-[#5C5650] hover:border-[#C4956A]/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="property_count"
                          value={opt.value}
                          checked={propertyCount === opt.value}
                          onChange={(e) => setPropertyCount(e.target.value)}
                          required
                          className="sr-only"
                        />
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            propertyCount === opt.value
                              ? "border-[#C4956A]"
                              : "border-[#D4CFC9]"
                          }`}
                        >
                          {propertyCount === opt.value && (
                            <span className="w-2 h-2 rounded-full bg-[#C4956A]" />
                          )}
                        </span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 카톡아이디 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                    연락받으실 카톡 아이디
                  </label>
                  <input
                    type="text"
                    name="kakao_id"
                    placeholder="카카오톡 아이디를 입력해주세요"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                  />
                </div>

                {/* 신청자 연령대 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                    신청자 연령대 (출생년도)
                  </label>
                  <input
                    type="text"
                    name="birth_year"
                    placeholder="예: 1990"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                  />
                </div>

                {/* 성별 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                    성별 <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {genderOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border cursor-pointer transition-all ${
                          gender === opt.value
                            ? "border-[#C4956A] bg-[#C4956A]/5 text-[#C4956A] font-medium"
                            : "border-[#E8E5E0] bg-white text-[#5C5650] hover:border-[#C4956A]/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={opt.value}
                          checked={gender === opt.value}
                          onChange={(e) => setGender(e.target.value)}
                          required
                          className="sr-only"
                        />
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            gender === opt.value
                              ? "border-[#C4956A]"
                              : "border-[#D4CFC9]"
                          }`}
                        >
                          {gender === opt.value && (
                            <span className="w-2 h-2 rounded-full bg-[#C4956A]" />
                          )}
                        </span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 결재관련 내용 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                    결제 관련 내용 <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {paymentOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border cursor-pointer transition-all ${
                          payment === opt.value
                            ? "border-[#C4956A] bg-[#C4956A]/5 text-[#C4956A] font-medium"
                            : "border-[#E8E5E0] bg-white text-[#5C5650] hover:border-[#C4956A]/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="payment_type"
                          value={opt.value}
                          checked={payment === opt.value}
                          onChange={(e) => setPayment(e.target.value)}
                          required
                          className="sr-only"
                        />
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            payment === opt.value
                              ? "border-[#C4956A]"
                              : "border-[#D4CFC9]"
                          }`}
                        >
                          {payment === opt.value && (
                            <span className="w-2 h-2 rounded-full bg-[#C4956A]" />
                          )}
                        </span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 입금자명 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                    입금자명 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="depositor_name"
                    required
                    placeholder="입금 시 사용된 이름"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                  />
                </div>

                {/* 알게된 경로 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                    알게된 경로 <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {referralOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border cursor-pointer transition-all ${
                          referral === opt.value
                            ? "border-[#C4956A] bg-[#C4956A]/5 text-[#C4956A] font-medium"
                            : "border-[#E8E5E0] bg-white text-[#5C5650] hover:border-[#C4956A]/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="referral_path"
                          value={opt.value}
                          checked={referral === opt.value}
                          onChange={(e) => setReferral(e.target.value)}
                          required
                          className="sr-only"
                        />
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            referral === opt.value
                              ? "border-[#C4956A]"
                              : "border-[#D4CFC9]"
                          }`}
                        >
                          {referral === opt.value && (
                            <span className="w-2 h-2 rounded-full bg-[#C4956A]" />
                          )}
                        </span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 경매 경력 */}
                <div>
                  <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                    경매 경력 <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {experienceOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border cursor-pointer transition-all ${
                          experience === opt.value
                            ? "border-[#C4956A] bg-[#C4956A]/5 text-[#C4956A] font-medium"
                            : "border-[#E8E5E0] bg-white text-[#5C5650] hover:border-[#C4956A]/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="auction_experience"
                          value={opt.value}
                          checked={experience === opt.value}
                          onChange={(e) => setExperience(e.target.value)}
                          required
                          className="sr-only"
                        />
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            experience === opt.value
                              ? "border-[#C4956A]"
                              : "border-[#D4CFC9]"
                          }`}
                        >
                          {experience === opt.value && (
                            <span className="w-2 h-2 rounded-full bg-[#C4956A]" />
                          )}
                        </span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 기타안내 사항 */}
                <div className="bg-[#FAFAF8] rounded-lg p-5 border border-[#E8E5E0]">
                  <h4 className="text-sm font-bold text-[#1C1917] mb-3 flex items-center gap-2">
                    <i className="ri-information-line text-[#C4956A]"></i>
                    기타 안내 사항
                  </h4>
                  <ol className="space-y-2 text-xs text-[#5C5650] leading-relaxed list-decimal list-inside">
                    <li>본 개인정보는 오로지 상담 용도로만 활용되며, 외부로 유출 또는 마케팅의 용도로 활용되지 않습니다.</li>
                    <li>참여 단톡방은 입금 확인 후 개인톡으로 전해드립니다.</li>
                    <li>포트폴리오의 제시와 방향성을 제안드리지만 종 선택에 따른 권리와 책임은 본인에게 있습니다.</li>
                  </ol>
                </div>

                {/* 개인정보 동의 */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      name="privacy_agree"
                      checked={privacyAgree}
                      onChange={(e) => setPrivacyAgree(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        privacyAgree
                          ? "bg-[#C4956A] border-[#C4956A]"
                          : "border-[#D4CFC9] group-hover:border-[#C4956A]/60"
                      }`}
                    >
                      {privacyAgree && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-[#5C5650] leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. <span className="text-red-400">*</span>
                  </span>
                </label>

                {/* Honeypot */}
                <div className="absolute opacity-0 pointer-events-none" style={{ left: "-9999px" }}>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {/* Error Message */}
                {formStatus === "error" && formError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    <p className="text-xs text-red-600">{formError}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={formStatus === "loading" || formStatus === "success"}
                  className="w-full py-3.5 text-sm font-semibold rounded-full transition-all whitespace-nowrap disabled:opacity-70 bg-[#C4956A] text-white hover:bg-[#B08050]"
                >
                  {formStatus === "loading" ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      제출 중...
                    </span>
                  ) : formStatus === "success" ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-check-line"></i>
                      신청 완료!
                    </span>
                  ) : (
                    "수강 신청하기"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}